/**
 * @copyright Copyright 2016-2019 The MathWorks, Inc.
 * @description ButtonBar is a module which used to create button bar for a dialog
 * @module mw-dialogs/view/ButtonBar
 */
define([
    "dojo/dom-class",
    "dojo/on",
    "mw-dialogs/ButtonEnum",
    "mw-log/Log"
], function (domClass, on, ButtonEnum, Log) {

    const supplementalButtonTypes = [
        ButtonEnum.TYPE.HELP,
        ButtonEnum.TYPE.SPL1,
        ButtonEnum.TYPE.SPL2
    ];

    const actionButtonTypes = [
        ButtonEnum.TYPE.DO_IT,
        ButtonEnum.TYPE.DONT_DO_IT,
        ButtonEnum.TYPE.CANCEL,
        ButtonEnum.TYPE.APPLY,
        ButtonEnum.TYPE.REVERT
    ];

    const buttonSize = 96;
    const padding = 5;

    /**
     * Place the buttons in the Action Button Area in the following order from left-to-right:
     *  1. [Do It]/Yes/OK
     *  2. [Don't Do It]/No
     *  3. Cancel or Close
     *  4. Apply (if present)
     *  5. Revert (if present)
     * <br />
     * Only allow one of same type of buttons exists, if several of same type of buttons be
     * specified, it will accept the first one
     */
    function sortActionButtons(buttons) {
        let sortedButtons = [];

        for (let i in actionButtonTypes) {
            sortedButtons[i] = [];
            for (let j in buttons) {
                if (buttons[j].type === actionButtonTypes[i]) {
                    sortedButtons[i].push(buttons[j]);
                }
            }
        }

        let uniqueButtons = [];

        for (let i in sortedButtons) {
            if (sortedButtons[i].length !== 0) {
                uniqueButtons.push(sortedButtons[i][0]);
            }
        }

        return uniqueButtons;
    }

    function createActionButtons(dialog, buttons, defaultActionButton) {
        let actionButtonBar = document.createElement("div");
        actionButtonBar.className = 'mwActionButtonBar';
        actionButtonBar.setAttribute("data-test-id", "dialog-action-button-bar");

        let actionButtons = sortActionButtons(buttons);
        let actionButton;

        for (let i in actionButtons) {

            actionButton = document.createElement("span");
            actionButton.className = 'mwButton';
            actionButton.tabIndex = "0";

            actionButton.textContent = actionButtons[i].label;

            actionButton.setAttribute("data-button-type", actionButtons[i].type);
            actionButton.setAttribute("aria-disabled", 'false');
            actionButton.setAttribute("role", "button");
            if (actionButtons[i]["data-test-id"]) {
                actionButton.setAttribute("data-test-id", actionButtons[i]["data-test-id"]);
            }

            dialog._trackEventListener(on(actionButton, 'click',
                emitButtonEventOnClick(`${actionButtons[i].type}ButtonClick`, dialog)
            ));

            if (defaultActionButton === actionButtons[i].type
                || defaultActionButton === actionButtons[i]
                || (defaultActionButton === undefined &&
                defaultActionButton !== ButtonEnum.NO_DEFAULT_ACTION && i === "0")) {
                dialog._defaultButton = actionButton;
            }

            actionButtonBar.appendChild(actionButton);
            dialog._buttonBar._buttonBarWidth += buttonSize;
        }

        return actionButtonBar;
    }

    function createSupplementalButtons(dialog, buttons) {
        let supplementalButtonBar = document.createElement("div");
        supplementalButtonBar.className = 'mwSplButtonBar';
        supplementalButtonBar.setAttribute("data-test-id", "dialog-supplemental-button-bar");

        let supplementalButton;

        for (let i in buttons) {

            supplementalButton = document.createElement("span");
            supplementalButton.className = 'mwButton';
            supplementalButton.tabIndex = "0";
            supplementalButton.setAttribute("role", "button");

            supplementalButton.textContent = buttons[i].label;

            supplementalButton.setAttribute("data-button-type", buttons[i].type);
            supplementalButton.setAttribute("aria-disabled", 'false');
            if (buttons[i]["data-test-id"]) {
                supplementalButton.setAttribute("data-test-id", buttons[i]["data-test-id"]);
            }

            dialog._trackEventListener(on(supplementalButton, 'click',
                emitButtonEventOnClick(`${buttons[i].type}ButtonClick`, dialog)
            ));

            supplementalButtonBar.appendChild(supplementalButton);
            dialog._buttonBar._buttonBarWidth += buttonSize;
        }

        return supplementalButtonBar;
    }

    /**
     * Emit button click event.
     *
     * @fires (Type)ButtonClick
     * @property {object} buttonEvent - Name of the button click event
     * @property {object} dialog - The dialog instance
     */
    function emitButtonEventOnClick(buttonEvent, dialog) {
        let dialogCloseFunction = () => {
            dialog.close();
        };

        return () => {
            on.emit(dialog.domNode, buttonEvent, {
                bubbles: false,
                close: dialogCloseFunction
            });
        };
    }

    function addButtonClickEventHandler(dialog, buttonType) {
        return on(dialog.domNode, `${buttonType}ButtonClick`, e => {
            let close = false;
            dialog._buttonEvents[buttonType].forEach(event => {
                if (typeof event.func === 'function') {
                    event.func();
                }
                close = close || event.dismiss;
            });
            if (dialog.domNode && close) {
                e.close();
            }
        });
    }

    function getButtonType(buttonNameOrType) {
        let buttonType = "";
        if (buttonNameOrType.hasOwnProperty("type")) {
            buttonType = buttonNameOrType.type;
        } else {
            buttonType = buttonNameOrType;
        }
        return buttonType;
    }

    /**
     * @alias mw-dialogs/view/ButtonBar
     */
    return {
        /**
         * Register event listener for button click event
         *
         * @static
         *
         * @param {Object} dialog - The dialog instance
         * @param {Object} buttonNameOrType - Button or Type of the button which the event
         * listener be registered. E.g., ButtonEnum.OK, ButtonEnum.TYPE.DO_IT
         * @param {Function} callBackFunction - The callback function when clicking on the button
         * @param {Boolean} dismiss - Whether to close the dialog when click on the button
         */
        addButtonEventListener (dialog, buttonNameOrType, callBackFunction, dismiss) {
            try {
                let buttonType = getButtonType(buttonNameOrType);

                // initialize button events
                if (dialog._buttonEvents[buttonType] === undefined) {
                    dialog._buttonEvents[buttonType] = [];
                }

                // add the event listener info to the event list of the button
                dialog._buttonEvents[buttonType].push({
                    func: callBackFunction,
                    dismiss: dismiss
                });

                // removes the previous event handler
                if (dialog._buttonEventHandlers[buttonType]) {
                    dialog._buttonEventHandlers[buttonType].remove();
                }

                let buttonNode = dialog._buttonBar.querySelector("span[data-button-type=" +
                    buttonType + "]");

                let isDisabled = buttonNode && buttonNode.getAttribute('aria-disabled');

                // Do not add event handler if disabled
                if (!isDisabled || isDisabled === "false") {
                    dialog._buttonEventHandlers[buttonType] =
                    addButtonClickEventHandler(dialog, buttonType);
                }
            } catch (e) {
                Log.log("Button or type of button is invalid.");
            }
        },

        /**
         * Enables an action or supplemental button, allowing interaction
         * @param {Object} dialog - The dialog instance
         * @param {Object} buttonNameOrType - Button or Type of the button which is used to tell
         *     which button in dialog to be set as enabled.  E.g., ButtonEnum.OK,
         *     ButtonEnum.TYPE.DO_IT
         */
        enableButton (dialog, buttonNameOrType) {
            let buttonType = getButtonType(buttonNameOrType);

            if (dialog._buttonEventHandlers[buttonType]) {
                dialog._buttonEventHandlers[buttonType].remove();
            }
            dialog._buttonEventHandlers[buttonType] =
                addButtonClickEventHandler(dialog, buttonType);

            let disabledButton = dialog._buttonBar.querySelector("span[data-button-type=" +
                buttonType + "]");
            domClass.remove(disabledButton, 'mwDisabled');
            disabledButton.setAttribute("tabIndex", "0");
            disabledButton.setAttribute("aria-disabled", 'false');
        },

        /**
         * Disables an action or supplemental button, preventing interaction
         * @param {Object} dialog - The dialog instance
         * @param {Object} buttonNameOrType - Button or Type of the button which is used to tell
         *     which button in dialog to be set as disabled.  E.g., ButtonEnum.OK,
         *     ButtonEnum.TYPE.DO_IT
         */
        disableButton (dialog, buttonNameOrType) {
            let buttonType = getButtonType(buttonNameOrType);

            if (dialog._buttonEventHandlers[buttonType]) {
                dialog._buttonEventHandlers[buttonType].remove();
            }

            let disabledButton = dialog._buttonBar.querySelector("span[data-button-type=" +
                buttonType + "]");
            domClass.add(disabledButton, 'mwDisabled');
            disabledButton.setAttribute("tabIndex", "-1");
            disabledButton.setAttribute("aria-disabled", 'true');
        },

        /**
         * Create button bar in dialog
         * @param {Object} dialog - The dialog instance
         * @param {Array} buttons - Array of button objects to be displayed on the dialog.
         * @param {Object | String} defaultActionButton - Button or Type of the button which
         * determines the default action button that maps to the Enter/Return keys
         * @static
         */
        createButtonBar (dialog, buttons, defaultActionButton) {
            let buttonBar = dialog._buttonBar = document.createElement("div");
            buttonBar.className = 'mwDialogButtonBar';
            buttonBar.setAttribute("data-test-id", "dialog-button-bar");
            dialog._buttonEvents = {};
            dialog._buttonEventHandlers = {};
            dialog._buttonBar._buttonBarWidth = padding * 2;
            let hasActionButtonBar = false, hasSupplementalButtonBar = false;

            let actionButtons = [];
            let supplementalButtons = [];

            buttons.forEach(button => {
                if (supplementalButtonTypes.indexOf(button.type) !== -1) {
                    supplementalButtons.push(button);
                } else if (actionButtonTypes.indexOf(button.type) !== -1) {
                    actionButtons.push(button);
                }
            });

            if (supplementalButtons.length > 0) {
                hasSupplementalButtonBar = true;
                buttonBar.appendChild(createSupplementalButtons(dialog, supplementalButtons));
            } else {
                domClass.add(buttonBar, "mwNoSplBtn");
            }

            if (actionButtons.length > 0) {
                hasActionButtonBar = true;
                buttonBar.appendChild(createActionButtons(dialog, actionButtons,
                    defaultActionButton));
            }

            if (hasActionButtonBar === true && hasSupplementalButtonBar === true) {
                dialog._buttonBar._buttonBarWidth += padding * 2;
            }

            return buttonBar;
        }
    };
});
